/**
 * Series de emision — la numeracion de los paises donde el correlativo lo lleva
 * el emisor y no la autoridad tributaria (hoy, Peru).
 *
 * Es el equivalente de FoliosTab para el otro modelo de numeracion. La
 * diferencia que se nota en la UI: aca no hay CAF que subir ni rango que se
 * agote, asi que no hay "folios disponibles" ni aviso de stock bajo. Lo que si
 * hay, y no existe en Chile, es que el usuario declara la serie.
 */
import { useState } from 'react'
import { AlertTriangle, Check, Loader2, Plus, Star, Trash2, X } from 'lucide-react'
import { inputCls } from '@/lib/formStyles'
import { DataTable } from '@/components/ds/DataTable'
import {
  useCreateDocumentSeries,
  useDeleteDocumentSeries,
  useDocumentSeries,
  useUpdateDocumentSeries,
  type DocumentSeries,
} from '../hooks/useConfig'

const DOC_TYPES: { value: DocumentSeries['document_type']; label: string; ejemplo: string }[] = [
  { value: 'FE', label: 'Factura', ejemplo: 'F001' },
  { value: 'BE', label: 'Boleta de venta', ejemplo: 'B001' },
  { value: 'NC', label: 'Nota de crédito', ejemplo: 'FC01' },
  { value: 'ND', label: 'Nota de débito', ejemplo: 'FD01' },
]

/** Las mismas cuatro que crea `setup_document_series`, para el arranque en un clic. */
const ESTANDAR_PE: { document_type: DocumentSeries['document_type']; series: string }[] = [
  { document_type: 'FE', series: 'F001' },
  { document_type: 'BE', series: 'B001' },
  { document_type: 'NC', series: 'FC01' },
  { document_type: 'ND', series: 'FD01' },
]

function mensajeDeError(err: any, fallback: string): string {
  const data = err?.response?.data
  if (typeof data?.error?.message === 'string') return data.error.message
  // Errores de campo del serializer: { next_correlative: ["..."] }
  if (data && typeof data === 'object') {
    for (const valor of Object.values(data)) {
      if (Array.isArray(valor) && typeof valor[0] === 'string') return valor[0]
      if (typeof valor === 'string') return valor
    }
  }
  return fallback
}

export function SeriesTab({ countryCode = 'PE' }: { countryCode?: string }) {
  const { data: series = [], isLoading } = useDocumentSeries()
  const crear = useCreateDocumentSeries()
  const actualizar = useUpdateDocumentSeries()
  const eliminar = useDeleteDocumentSeries()

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [creando, setCreando] = useState(false)
  const [nuevoTipo, setNuevoTipo] = useState<DocumentSeries['document_type']>('FE')
  const [nuevaSerie, setNuevaSerie] = useState('')
  const [nuevoCorrelativo, setNuevoCorrelativo] = useState('1')
  // Ediciones locales del correlativo, por id de serie.
  const [locales, setLocales] = useState<Record<string, string>>({})
  const [guardando, setGuardando] = useState<Set<string>>(new Set())

  const avisar = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 5000)
  }

  const marcarGuardando = (id: string, activo: boolean) =>
    setGuardando(prev => {
      const s = new Set(prev)
      if (activo) s.add(id)
      else s.delete(id)
      return s
    })

  const handleCrear = () => {
    const serie = nuevaSerie.trim().toUpperCase()
    if (!serie) return avisar('Ingrese la serie, por ejemplo F001.', 'error')
    const correlativo = parseInt(nuevoCorrelativo, 10)
    if (isNaN(correlativo) || correlativo < 1) {
      return avisar('El correlativo debe ser un número mayor o igual a 1.', 'error')
    }

    crear.mutate(
      { document_type: nuevoTipo, series: serie, next_correlative: correlativo },
      {
        onSuccess: () => {
          avisar(`Serie ${serie} creada.`, 'success')
          setCreando(false)
          setNuevaSerie('')
          setNuevoCorrelativo('1')
        },
        onError: (err: any) => avisar(mensajeDeError(err, 'No se pudo crear la serie.'), 'error'),
      }
    )
  }

  const handleCrearEstandar = async () => {
    let creadas = 0
    for (const s of ESTANDAR_PE) {
      if (series.some(x => x.document_type === s.document_type && x.series === s.series)) continue
      try {
        await crear.mutateAsync({ ...s, next_correlative: 1 })
        creadas++
      } catch (err: any) {
        avisar(mensajeDeError(err, `No se pudo crear ${s.series}.`), 'error')
        return
      }
    }
    avisar(
      creadas > 0 ? `${creadas} serie${creadas > 1 ? 's' : ''} creada${creadas > 1 ? 's' : ''}.`
                  : 'Las series estándar ya estaban creadas.',
      'success'
    )
  }

  /**
   * El correlativo solo se guarda al salir del campo y solo si subio: bajarlo
   * repetiria numeros ya emitidos. El backend lo rechaza igual — esto es para
   * que el usuario lo sepa antes de mandar el pedido.
   */
  const handleCorrelativoBlur = (s: DocumentSeries) => {
    const raw = locales[s.id]
    if (raw === undefined) return
    const limpiar = () => setLocales(prev => { const n = { ...prev }; delete n[s.id]; return n })

    const valor = parseInt(raw, 10)
    if (isNaN(valor) || valor === s.next_correlative) return limpiar()
    if (valor < 1) {
      avisar('El correlativo debe ser mayor o igual a 1.', 'error')
      return limpiar()
    }
    if (valor < s.next_correlative) {
      avisar(
        `El correlativo no puede bajar de ${s.next_correlative} a ${valor}: repetiría ` +
        `números que ya se emitieron.`,
        'error'
      )
      return limpiar()
    }

    marcarGuardando(s.id, true)
    actualizar.mutate(
      { id: s.id, data: { next_correlative: valor } },
      {
        onSuccess: () => {
          marcarGuardando(s.id, false)
          limpiar()
          avisar(`${s.series} arranca ahora en ${valor}.`, 'success')
        },
        onError: (err: any) => {
          marcarGuardando(s.id, false)
          limpiar()
          avisar(mensajeDeError(err, 'No se pudo actualizar el correlativo.'), 'error')
        },
      }
    )
  }

  const handleToggle = (s: DocumentSeries, campo: 'is_default' | 'is_active') => {
    marcarGuardando(s.id, true)
    actualizar.mutate(
      { id: s.id, data: { [campo]: !s[campo] } },
      {
        onSuccess: () => marcarGuardando(s.id, false),
        onError: (err: any) => {
          marcarGuardando(s.id, false)
          avisar(mensajeDeError(err, 'No se pudo actualizar la serie.'), 'error')
        },
      }
    )
  }

  const handleEliminar = (s: DocumentSeries) => {
    eliminar.mutate(s.id, {
      onSuccess: () => avisar(`Serie ${s.series} eliminada.`, 'success'),
      onError: (err: any) => avisar(mensajeDeError(err, 'No se pudo eliminar la serie.'), 'error'),
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-content-subtle" size={28} />
      </div>
    )
  }

  const sinSeries = series.length === 0
  const tiposSinSerieActiva = DOC_TYPES.filter(
    t => !series.some(s => s.document_type === t.value && s.is_active)
  )

  return (
    <div className="max-w-3xl space-y-4">
      {toast && (
        <div
          role="status"
          className={`fixed top-4 right-4 z-[100] px-4 py-2 rounded-lg text-sm shadow-lg max-w-md ${
            toast.type === 'success' ? 'bg-success-fill text-white' : 'bg-danger-fill text-white'
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="p-4 bg-glass backdrop-blur-glass border border-divider rounded-card text-sm text-content shadow-card">
        <p className="font-medium text-primary dark:text-primary-hover">Series de emisión</p>
        <p className="text-xs mt-0.5 text-content-muted">
          {countryCode === 'PE'
            ? 'En Perú la serie la declara el contribuyente y el correlativo lo lleva tu sistema: ' +
              'SUNAT no autoriza rangos, así que no hay CAF que cargar. Cada documento sale numerado ' +
              'como serie + correlativo, por ejemplo F001-00000452.'
            : 'La serie la declara el emisor y el correlativo lo lleva Clobi. No hay rango que ' +
              'autorice la autoridad tributaria.'}
        </p>
      </div>

      {sinSeries && (
        <div className="p-4 bg-glass backdrop-blur-glass border border-divider rounded-card shadow-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-warning-solid shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm font-medium text-content">Todavía no hay ninguna serie configurada</p>
              <p className="text-xs text-content-muted mt-0.5">
                Sin una serie, los documentos no se pueden emitir: el pipeline se detiene y el
                documento vuelve a borrador. Si no hay una numeración heredada de otro sistema,
                estas cuatro son las que se usan habitualmente.
              </p>
              {countryCode === 'PE' && (
                <button
                  onClick={handleCrearEstandar}
                  disabled={crear.isPending}
                  className="mt-3 px-3 py-1.5 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-60"
                >
                  {crear.isPending ? 'Creando…' : 'Crear F001, B001, FC01 y FD01'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!sinSeries && tiposSinSerieActiva.length > 0 && (
        <div className="p-3 bg-glass backdrop-blur-glass border border-divider rounded-card flex items-start gap-2 shadow-card">
          <AlertTriangle className="text-warning-solid shrink-0 mt-0.5" size={16} />
          <p className="text-xs text-content-muted">
            Sin serie activa para{' '}
            <span className="text-content font-medium">
              {tiposSinSerieActiva.map(t => t.label.toLowerCase()).join(', ')}
            </span>
            . Esos documentos no se van a poder emitir hasta que tengan una.
          </p>
        </div>
      )}

      {!sinSeries && (
        <DataTable
          rows={series}
          rowKey={s => s.id}
          minWidth={900}
          caption="Series de emision configuradas"
          // Una serie desactivada se atenua ENTERA: atenuar celda por celda
          // dejaria el fondo y los bordes de la fila sin atenuar.
          rowClassName={s => (s.is_active ? undefined : 'opacity-50')}
          columns={[
            { key: 'document_type_label', header: 'Tipo de documento', sortable: true },
            {
              key: 'series', header: 'Serie', sortable: true, width: 96,
              render: s => <span className="font-mono text-content">{s.series}</span>,
            },
            {
              key: 'next_correlative', header: 'Próximo correlativo', width: 128,
              render: s => {
                const valorActual = locales[s.id] ?? String(s.next_correlative)
                return (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      value={valorActual}
                      onChange={e => setLocales(prev => ({ ...prev, [s.id]: e.target.value }))}
                      onBlur={() => handleCorrelativoBlur(s)}
                      aria-label={`Próximo correlativo de la serie ${s.series}`}
                      className="w-24 px-2 py-1 bg-surface border border-divider rounded text-sm text-content focus:outline-none focus:ring-2 focus:ring-focus-ring"
                    />
                    {guardando.has(s.id) && <Loader2 className="animate-spin text-content-subtle" size={14} />}
                  </div>
                )
              },
            },
            {
              key: 'next_number_preview', header: 'Próximo número', width: 160,
              render: s => <span className="font-mono text-content-muted">{s.next_number_preview}</span>,
            },
            {
              key: 'is_default',
              header: (
                <span title="La que se usa al emitir cuando hay mas de una serie del mismo tipo">
                  Por defecto
                </span>
              ),
              width: 96,
              render: s => (
                <div className="text-center">
                  <button
                    onClick={() => handleToggle(s, 'is_default')}
                    disabled={s.is_default}
                    title={s.is_default ? 'Es la serie por defecto' : 'Usar esta serie por defecto'}
                    aria-label={`Marcar ${s.series} como serie por defecto`}
                    className={`p-1 rounded ${s.is_default ? 'text-warning-solid' : 'text-content-subtle hover:text-warning-solid'}`}
                  >
                    <Star size={16} fill={s.is_default ? 'currentColor' : 'none'} />
                  </button>
                </div>
              ),
            },
            {
              key: 'is_active', header: 'Activa', width: 80,
              render: s => (
                <div className="text-center">
                  <button
                    onClick={() => handleToggle(s, 'is_active')}
                    title={s.is_active ? 'Desactivar' : 'Activar'}
                    aria-label={`${s.is_active ? 'Desactivar' : 'Activar'} la serie ${s.series}`}
                    className={`p-1 rounded ${s.is_active ? 'text-success-content-soft' : 'text-content-subtle'}`}
                  >
                    {s.is_active ? <Check size={16} /> : <X size={16} />}
                  </button>
                </div>
              ),
            },
            {
              key: 'eliminar', header: <span className="sr-only">Eliminar</span>, width: 64,
              render: s => (
                <div className="text-center">
                  {/* Si se puede eliminar lo decide el BACKEND (`can_delete`), no el
                      correlativo: una serie que arranca en 1000 porque continua la
                      numeracion de otro sistema no emitio 999 documentos. Y una que ya
                      emitio conserva su correlativo: es el registro de hasta donde llego. */}
                  <button
                    onClick={() => handleEliminar(s)}
                    disabled={!s.can_delete}
                    title={
                      s.can_delete
                        ? 'Eliminar'
                        : `No se puede eliminar: ya numeró ${s.issued_count} documento(s). Descativarla.`
                    }
                    aria-label={`Eliminar la serie ${s.series}`}
                    className="p-1 rounded text-content-subtle hover:text-danger-content-soft disabled:opacity-30 disabled:hover:text-content-subtle"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ),
            },
          ]}
        />
      )}

      {creando ? (
        <div className="p-4 bg-glass backdrop-blur-glass border border-divider rounded-card space-y-3 shadow-card">
          <p className="text-sm font-medium text-content">Nueva serie</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-xs text-content-muted mb-1">Tipo de documento</span>
              <select
                value={nuevoTipo}
                onChange={e => setNuevoTipo(e.target.value as DocumentSeries['document_type'])}
                className={inputCls}
              >
                {DOC_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-content-muted mb-1">
                Serie{countryCode === 'PE' && ` (ej. ${DOC_TYPES.find(t => t.value === nuevoTipo)?.ejemplo})`}
              </span>
              <input
                value={nuevaSerie}
                onChange={e => setNuevaSerie(e.target.value.toUpperCase())}
                placeholder={DOC_TYPES.find(t => t.value === nuevoTipo)?.ejemplo}
                maxLength={10}
                className={`${inputCls} font-mono`}
              />
            </label>
            <label className="block">
              <span className="block text-xs text-content-muted mb-1">Próximo correlativo</span>
              <input
                type="number"
                min={1}
                value={nuevoCorrelativo}
                onChange={e => setNuevoCorrelativo(e.target.value)}
                className={inputCls}
              />
            </label>
          </div>
          <p className="text-xs text-content-muted">
            Si viene de otro sistema, ingrese el número que sigue al último emitido allá: el
            correlativo no se puede bajar después.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleCrear}
              disabled={crear.isPending}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-60"
            >
              {crear.isPending ? 'Creando…' : 'Crear serie'}
            </button>
            <button
              onClick={() => { setCreando(false); setNuevaSerie('') }}
              className="px-3 py-1.5 text-sm rounded-lg border border-divider text-content hover:bg-surface-muted"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setCreando(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-divider text-content hover:bg-surface-muted"
        >
          <Plus size={15} /> Nueva serie
        </button>
      )}
    </div>
  )
}
